import React, { useCallback, useRef } from 'react'

type UseLongPressType = {
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => void
    onPointerUp: (e: React.PointerEvent<HTMLElement>) => void
    onPointerLeave: (e: React.PointerEvent<HTMLElement>) => void
}

type OnClickType = {
    onClick: () => void
}

type UseLongPressProps = {
    onLongPress?: () => void
    onPress?: (event?: PointerEvent) => void
    delay?: number
}

const usePointerEvents = ({
    onLongPress,
    onPress,
    delay = 500,
}: UseLongPressProps): UseLongPressType | OnClickType | undefined => {
    const [longPressTriggered, setLongPressTriggered] = React.useState(false)
    const timeout = useRef<ReturnType<typeof setTimeout> | undefined>()

    const start = useCallback(
        (event: React.PointerEvent<HTMLElement>, onLongPress: () => void) => {
            timeout.current = setTimeout(() => {
                onLongPress()
                setLongPressTriggered(true)
            }, delay)
        },
        [delay]
    )

    const clear = useCallback(
        (event, shouldTriggerClick = true) => {
            timeout.current && clearTimeout(timeout.current)
            shouldTriggerClick && !longPressTriggered && onPress && onPress(event)
            setLongPressTriggered(false)
        },
        [longPressTriggered, onPress]
    )

    if (onLongPress) {
        return {
            onPointerDown: (e: React.PointerEvent<HTMLElement>): void => start(e, onLongPress),
            onPointerUp: (e: React.PointerEvent<HTMLElement>): void => clear(e),
            onPointerLeave: (e: React.PointerEvent<HTMLElement>): void => clear(e, false),
        }
    }

    return onPress
        ? {
              onClick: onPress,
          }
        : undefined
}

export default usePointerEvents
